import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useOrderContext } from '../../../contexts/OrderContext';
import { usePositionContext } from '../../../contexts/PositionContext';
import { useAccount } from '../../../contexts/AccountContext';
import { useAccountType } from '../../../contexts/AccountTypeContext';

interface RSIStrategyProps {
  symbol: string;
  currentPrice?: string;
  onSignalUpdate?: (signal: 'buy' | 'sell' | null) => void;
  onConfigUpdate?: (config: any) => void;
  isRunning?: boolean;
}

interface RSIConfig {
  period: number;
  overbought: number;
  oversold: number;
  isActive: boolean;
  tradeAmountPercent: number;
}

const RSIStrategy: React.FC<RSIStrategyProps> = ({ 
  symbol, 
  currentPrice, 
  onSignalUpdate, 
  onConfigUpdate, 
  isRunning: externalIsRunning 
}) => {
  const { theme } = useTheme();
  const { setOrderDetails, setShowOrderConfirmation, showConfirmationPopup } = useOrderContext();
  const { addOpenPosition } = usePositionContext();
  const { accountBalance } = useAccount();
  const { accountType } = useAccountType();

  const [config, setConfig] = useState<RSIConfig>({
    period: 14,
    overbought: 70,
    oversold: 30,
    isActive: false,
    tradeAmountPercent: 10 // Default 10% of balance
  });
  
  const [currentSignal, setCurrentSignal] = useState<'buy' | 'sell' | null>(null);
  const [lastSignal, setLastSignal] = useState<'buy' | 'sell' | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  // Use external isRunning if provided, otherwise use internal state
  const actualIsRunning = externalIsRunning !== undefined ? externalIsRunning : isRunning;

  // Notify parent component when config changes
  useEffect(() => {
    onConfigUpdate?.(config);
  }, [config, onConfigUpdate]);

  // Simulate RSI calculation (in real implementation, this would use actual market data)
  const calculateRSI = useCallback(() => {
    if (!currentPrice || !actualIsRunning) {
      return;
    }
    
    const price = parseFloat(currentPrice);
    
    // Simplified RSI logic for demonstration
    // In real implementation, you would calculate RSI based on price changes
    const randomRSI = Math.random() * 100; // Simulate RSI value 0-100
    

    
    // Simulate RSI signals
    // Buy when RSI is oversold (below 30)
    // Sell when RSI is overbought (above 70)
    if (randomRSI < config.oversold) {
      setCurrentSignal('buy');
      setLastSignal('buy');
      onSignalUpdate?.('buy');
    } else if (randomRSI > config.overbought) {
      setCurrentSignal('sell');
      setLastSignal('sell');
      onSignalUpdate?.('sell');
    } else {
      setCurrentSignal(null);
      onSignalUpdate?.(null);
    }
  }, [currentPrice, actualIsRunning, config.period, config.overbought, config.oversold, onSignalUpdate]);

  // Execute AI trade based on signal
  const executeAITrade = useCallback((signal: 'buy' | 'sell') => {
    if (!currentPrice || !accountBalance) {
      return;
    }

    const currentBalance = accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance;
    const tradeAmountUSD = currentBalance * (config.tradeAmountPercent / 100);
    const amount = tradeAmountUSD / parseFloat(currentPrice);



    // Check if we have enough balance
    if (tradeAmountUSD > currentBalance) {
      return;
    }

    try {
      const positionData = {
        symbol: symbol,
        type: (signal === 'buy' ? 'Buy' : 'Sell') as 'Buy' | 'Sell',
        size: amount.toFixed(6),
        entry: currentPrice,
        current: currentPrice,
        pnl: '0.00',
        pnlPercent: '0.00%',
        leverage: 1
      };

      addOpenPosition(positionData);
    } catch (error) {
      console.error('Error adding position:', error);
      
      // Fallback to order confirmation modal
      if (setOrderDetails) {
        setOrderDetails({
          symbol: symbol,
          side: signal,
          type: 'market',
          amount: parseFloat(amount.toFixed(6)),
          price: parseFloat(currentPrice),
          leverage: 1,
          stopPrice: undefined
        });
        
        if (showConfirmationPopup) {
          setShowOrderConfirmation(true);
        }
      }
    }
  }, [currentPrice, accountBalance, accountType, config.tradeAmountPercent, symbol, addOpenPosition, setOrderDetails, setShowOrderConfirmation, showConfirmationPopup]);

  // Monitor for signal changes and execute trades
  useEffect(() => {
    if (currentSignal && currentSignal !== lastSignal) {
      executeAITrade(currentSignal);
    }
  }, [currentSignal, lastSignal, executeAITrade]);

  // Calculate RSI periodically
  useEffect(() => {
    if (!actualIsRunning) return;
    
    const interval = setInterval(calculateRSI, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [calculateRSI, actualIsRunning]);

  const handleConfigChange = (field: keyof RSIConfig, value: number | boolean) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      {/* Strategy Configuration */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Period:</span>
          <input
            type="number"
            value={config.period}
            onChange={(e) => handleConfigChange('period', parseInt(e.target.value))}
            className="w-16 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            min="5"
            max="50"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Overbought:</span>
          <input
            type="number"
            value={config.overbought}
            onChange={(e) => handleConfigChange('overbought', parseInt(e.target.value))}
            className="w-16 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            min="60"
            max="90"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Oversold:</span>
          <input
            type="number"
            value={config.oversold}
            onChange={(e) => handleConfigChange('oversold', parseInt(e.target.value))}
            className="w-16 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
            min="10"
            max="40"
          />
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-xs theme-text-quaternary">Trade Amount:</span>
          <div className="flex items-center space-x-1">
            <input
              type="number"
              value={config.tradeAmountPercent}
              onChange={(e) => handleConfigChange('tradeAmountPercent', parseFloat(e.target.value))}
              className="w-12 px-2 py-1 text-xs bg-transparent border theme-border rounded text-right theme-text-primary"
              min="1"
              max="100"
              step="1"
            />
            <span className="text-xs theme-text-quaternary">%</span>
          </div>
        </div>
        
        {/* Trade Amount Display */}
        {accountBalance && (
          <div className="flex items-center justify-between pt-2 border-t theme-border">
            <span className="text-xs theme-text-quaternary">Trade Value:</span>
            <span className="text-xs font-medium theme-text-primary">
              ${((accountType === 'demo' ? accountBalance.demoBalance : accountBalance.liveBalance) * config.tradeAmountPercent / 100).toFixed(2)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RSIStrategy;
